import { Save } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Grid,
  Paper,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { DateCalendar, PickersDay } from "@mui/x-date-pickers";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import React, { Fragment } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import useSubmit from "../../../../../master/species/hooks/useSubmit";
import { useTranslation } from "react-i18next";
import {
  SINGLE_THERAPY_KEY,
  THERAPY_DAY_KEY,
} from "../../../../../../enums/QueryKeys";
import { PUT_THERAPY_DAY } from "../../../../../../enums/ApiPaths";
import { trimText } from "../../../../../../utils/Functions";
import { CRIMSON_RED } from "../../../../../../enums/Colors";
import { getEarliestDate } from "../../../../../../utils/DateTimeRepository";

export default function TherapySchedule({ days = [], isArchived = false }) {
  return <TherapyDays days={days} isArchived={isArchived} />;
}

function TherapyDays({ days = [], isArchived = false }) {
  const url = PUT_THERAPY_DAY;
  const submitId = null;
  const { id } = useParams();
  const submittion = useSubmit(submitId, url, THERAPY_DAY_KEY + id);
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const sortedDays = React.useMemo(() => {
    return [...days].sort(
      (a, b) =>
        dayjs(a.date, "DD/MM/YYYY").valueOf() -
        dayjs(b.date, "DD/MM/YYYY").valueOf(),
    );
  }, [days]);

  const { control, handleSubmit, watch } = useForm({
    defaultValues: {
      days: sortedDays.map((d) => ({
        id: d.id,
        date: d.date,
        morningDoseDone: d.morningDone,
        eveningDoseDone: d.eveningDone,
        morningDosage: d.morningDosage,
        eveningDosage: d.eveningDosage,
        eveningDisabled: d.eveningDisabled,
        morningDisabled: d.morningDisabled,
      })),
    },
  });

  const { fields } = useFieldArray({
    control,
    name: "days",
  });

  const watchedDays = watch("days");

  const therapyDays = days.map((d, i) => ({
    date: dayjs(d.date, "DD/MM/YYYY"),
    isGreen:
      watchedDays?.[i]?.morningDoseDone && watchedDays?.[i]?.eveningDoseDone,
  }));

  async function onSubmit(data) {
    const submitObj = data?.days;

    if (Number(submitObj.length) === 0) {
      return;
    }

    const success = await submittion.submitCallback(submitObj);

    if (success) {
      queryClient.invalidateQueries([SINGLE_THERAPY_KEY + id]);
    }
  }

  return (
    <Fragment>
      {/* Main content with better layout */}
      <Grid container spacing={4}>
        {/* Calendar Section */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              height: "100%",
            }}
          >
            <Typography
              variant="h6"
              fontWeight="500"
              mb={3}
              color="text.primary"
            >
              {t("TherapyDaysCalendar")}
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <DateCalendar
                referenceDate={getEarliestDate(
                  days.map((d) => d.date),
                  "DD/MM/YYYY",
                )}
                sx={{
                  "& .MuiPickersDay-root": {
                    fontSize: "0.875rem",
                  },
                }}
                slots={{
                  day: (props) => {
                    const match = therapyDays.find((d) =>
                      d.date.isSame(props.day, "day"),
                    );
                    return (
                      <CustomDay
                        {...props}
                        selected={match && !match?.isGreen}
                        isGreen={match?.isGreen}
                      />
                    );
                  },
                }}
              />
            </Box>

            {/* Legend */}
            <Box
              sx={{ display: "flex", gap: 3, mt: 3, justifyContent: "center" }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: "success.main",
                  }}
                />
                <Typography variant="caption">{t("Completed")}</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                  }}
                />
                <Typography variant="caption">{t("Scheduled")}</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Table Section */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              height: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography variant="h6" fontWeight="500" color="text.primary">
                {t("TherapyHistory")}
              </Typography>
              {isArchived ? null : (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Save />}
                  onClick={handleSubmit(onSubmit)}
                >
                  {t("Save")}
                </Button>
              )}
            </Box>

            <TableContainer sx={{ maxHeight: 600 }}>
              <Table
                sx={{
                  minWidth: 650,
                  "& .MuiTableCell-root": {
                    py: 1.5,
                  },
                }}
                aria-label="therapy schedule table"
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>{t("Date")}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {t("Morning")}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {t("Evening")}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {t("MorningDose")}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {t("EveningDose")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fields.map((field, index) => (
                    <TableRow key={field.id} hover>
                      <TableCell>{field.date}</TableCell>

                      <TableCell align="right">
                        <Controller
                          control={control}
                          name={`days.${index}.morningDoseDone`}
                          render={({ field: renderField }) => {
                            const { morningDisabled } = field;
                            return (
                              <Checkbox
                                checked={renderField.value}
                                disabled={morningDisabled || isArchived}
                                onChange={(e) =>
                                  renderField.onChange(e.target.checked)
                                }
                              />
                            );
                          }}
                        />
                      </TableCell>

                      <TableCell align="right">
                        <Controller
                          disabled={isArchived}
                          control={control}
                          name={`days.${index}.eveningDoseDone`}
                          render={({ field: renderField }) => {
                            const { eveningDisabled } = field;
                            return (
                              <Checkbox
                                checked={renderField.value}
                                disabled={eveningDisabled || isArchived}
                                onChange={(e) =>
                                  renderField.onChange(e.target.checked)
                                }
                              />
                            );
                          }}
                        />
                      </TableCell>

                      <TableCell align="right">
                        {field.morningDosage == "" || !field.morningDosage ? (
                          "-"
                        ) : (
                          <Tooltip title={field.morningDosage}>
                            {trimText(field.morningDosage, 30)}
                          </Tooltip>
                        )}
                      </TableCell>

                      <TableCell align="right">
                        {field.eveningDosage == "" || !field.eveningDosage ? (
                          "-"
                        ) : (
                          <Tooltip title={field.eveningDosage}>
                            {trimText(field.eveningDosage, 30)}
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Fragment>
  );
}

export const CustomDay = styled(PickersDay, {
  shouldForwardProp: (prop) => prop !== "isGreen" && prop !== "isRed",
})(({ isGreen, isRed }) => ({
  ...(isGreen &&
    !isRed && {
      backgroundColor: "#2e7d32",
      color: "white",
    }),

  ...(isRed && {
    backgroundColor: "#d32f2f",
    color: "white",
  }),
}));
